<template>
  <main class="DashboardView">
    <div class="container">
      <AppLoading v-if="loading" />

      <div v-if="!loading">
        <section class="DashboardView__chart mb-4">
          <button type="button" class="DashboardView__refresh btn btn-outline-primary btn-sm" @click="refresh()" title="Refresh data">
            <i class="bi bi-arrow-repeat" />
          </button>

          <!-- ============================================================= -->
          <!-- CHART TOOLBAR                                                 -->
          <!-- LEFT: mode. RIGHT: benchmark picker, present only in % mode.  -->
          <!-- ============================================================= -->
          <div class="DashboardView__toolbar">
            <div class="btn-group btn-group-sm" role="group" aria-label="Chart mode">
              <button
                type="button"
                class="btn"
                :class="mode === 'value' ? 'btn-primary' : 'btn-outline-primary'"
                @click="setMode('value')"
              >
                <i class="bi bi-currency-dollar" /> Value
              </button>
              <button
                type="button"
                class="btn"
                :class="mode === 'performance' ? 'btn-primary' : 'btn-outline-primary'"
                @click="setMode('performance')"
              >
                <i class="bi bi-percent" /> Performance
              </button>
            </div>

            <!-- Absent in $ mode rather than disabled: a greyed-out control invites the
                 question "why can't I tick this?", which has no useful answer here —
                 there is nothing to compare a dollar balance against. -->
            <!-- The swatch colours come in as CSS custom properties rather than SASS
                 literals: they are the same theme lookup the chart datasets use, so a
                 swatch can never end up labelling the wrong line. -->
            <div class="DashboardView__compare" v-if="mode === 'performance'" :style="swatchVars">
              <FormCheckboxes
                v-model="compare"
                label="Compare with:"
                :options="compareOptions"
                inline
              />
            </div>
          </div>

          <ClientAccountChart v-show="mode === 'value'" :account="account" />

          <!-- Mounted on first use, then kept alive: toggling back and forth must not
               refire a request the endpoint deliberately does not cache. -->
          <ClientPerformanceChart
            v-if="performanceMounted"
            v-show="mode === 'performance'"
            :compare="compare"
          />
        </section>

        <div class=" mb-4" v-if="allocations">
          <draggable
            item-key="id"
            class="DashboardView__allocations"
            ghost-class="DashboardView__allocations__item--ghost"
            handle=".DashboardView__allocations__item__handle"
            :list="allocations"
            @end="onDragEnd"
          >
            <template #item="{ element }">
              <div class="DashboardView__allocations__item">
                <div class="DashboardView__allocations__item__handle">
                  <i class="bi bi-arrows-move" />
                </div>
                <AllocationCard :allocation="element" />
              </div>
            </template>
          </draggable>
        </div>
      </div>
    </div>
  </main>
</template>

<script>
  import AppLoading from '@/components/AppLoading.vue'
  import AllocationCard from '@/components/AllocationCard.vue'
  import ClientAccountChart from '@/components/ClientAccountChart.vue'
  import ClientPerformanceChart from '@/components/ClientPerformanceChart.vue'
  import FormCheckboxes from '@/components/base/FormCheckboxes'

  import allocationService from '@/services/allocations.service'

  import theTheme from '@/theme.js'
  import { benchmarkOptions, benchmarkSwatchVars } from '@/utils/benchmarks'

  import useToast from '@/plugins/toast/useToast'
  import draggable from 'vuedraggable'

  export default {
    components: {
      AppLoading,
      AllocationCard,
      ClientAccountChart,
      ClientPerformanceChart,
      FormCheckboxes,
      draggable
    },

    setup() {
      return {
        toast$: useToast()
      }
    },

    data() {
      return {
        loading: false,
        account: null,
        allocations: null,

        /* 'value' is the default on purpose: the client arrives to exactly the dashboard
           he saw yesterday, and opts into the percentage view. */

        mode: 'value',
        performanceMounted: false,

        /* All three unticked. The client's own curve is the subject; a benchmark is
           something he asks for. */

        compare: [],
        compareOptions: benchmarkOptions(),
        theme: null,

        enabled: true,
        dragging: false
      }
    },

    computed: {
      swatchVars() {
        return benchmarkSwatchVars(this.theme)
      }
    },

    async mounted() {
      this.theme = await theTheme.ready()
      this.load()
    },

    methods: {
      async load(force) {
        this.loading = true

        const response = await allocationService.fetchDashboard(force)

        this.account = response.account
        this.allocations = response.allocations

        this.loading = false
      },

      setMode(mode) {
        this.mode = mode

        if (mode === 'performance') {
          this.performanceMounted = true
        }
      },

      refresh() {
        this.load(true)
      },

      onDragEnd() {
        this.toast$.success('Reordering', 'The new ordering has been saved.')
        allocationService.savePositions(this.allocations)
      }
    }
  }
</script>

<style lang="sass" scoped>
  @import '@/sass/styles.vars.all.sass'

  .DashboardView__chart
    position: relative

  .DashboardView__refresh
    position: absolute
    right: 0
    top: 1em
    z-index: 1

  .DashboardView__toolbar
    display: flex
    flex-wrap: wrap
    align-items: flex-start
    justify-content: space-between
    gap: $spacer-2
    margin-bottom: $spacer-3
    // The refresh button is absolutely positioned at right:0 of this section, so the
    // toolbar has to keep its own right edge clear of it rather than run underneath.
    padding-right: 3rem

  .DashboardView__compare
    :deep(.form-group)
      display: flex
      align-items: center
      gap: $spacer-2
      margin-bottom: 0

    :deep(.form-label)
      margin-bottom: 0
      white-space: nowrap
      font-weight: bold
      color: $primary

    :deep(.form-check)
      margin-bottom: 0

    // Colour swatch beside each benchmark label. Keyed off the `for` attribute FormBoxes
    // builds from the series key (normalizeKey() -> '<generated-id>-btcusd'), so the
    // suffix is stable even though the id prefix is random per mount.
    // Redundant with the chart legend on purpose: with four series the mapping is worth
    // stating twice, and this is the one place it is stated at the moment of choosing.
    :deep(.form-check-label)
      display: inline-flex
      align-items: center
      gap: .45em

      &::before
        content: ''
        display: inline-block
        width: 1.1em
        height: 3px
        border-radius: 2px
        background: $gray-400

    :deep(label[for$="-btcusd"])::before
      background: var(--swatch-btcusd)

    :deep(label[for$="-sp500"])::before
      background: var(--swatch-sp500)

    :deep(label[for$="-gold"])::before
      background: var(--swatch-gold)

  .DashboardView__allocations
    display: flex
    gap: $spacer
    flex-wrap: wrap
    justify-content: center
    align-content: center
    align-items: center

  .DashboardView__allocations__item
    position: relative
    flex-grow: 1
    flex-shrink: 1
    flex-basis: 100%
    width: 100%

    &--ghost
      opacity: .2

    @include media-breakpoint-up(md)
      flex-grow: 0
      flex-shrink: 1
      flex-basis: calc(50% - #{$spacer})
      width: 0

    @include media-breakpoint-up(lg)
      flex-grow: 0
      flex-shrink: 1
      flex-basis: calc(33.33% - #{$spacer})

    @include media-breakpoint-up(xl)
      flex-grow: 0
      flex-shrink: 1
      flex-basis: calc(20% - #{$spacer})

  .DashboardView__allocations__item__handle
    cursor: move
    position: absolute
    top: 1em
    right: 1em
    z-index: 1
    opacity: 0
    transition: .25s

    .DashboardView__allocations__item:hover &
      opacity: 1
</style>
